import { useState } from 'react';
import type { FlashcardReportReason } from '../../../api/flashcardLibraryService';
import { useTranslation } from '../../../i18n/useTranslation';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (reason: FlashcardReportReason, notes?: string) => Promise<void> | void;
}

export default function FlashcardReportDialog({ open, onClose, onSubmit }: Props) {
  const { t } = useTranslation();
  const [reason, setReason] = useState<FlashcardReportReason>('Incorrect');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const REASONS: { value: FlashcardReportReason; label: string }[] = [
    { value: 'Incorrect',  label: t('App.Reader.Flashcard.Report.ReasonIncorrect') },
    { value: 'Misleading', label: t('App.Reader.Flashcard.Report.ReasonMisleading') },
    { value: 'Offensive',  label: t('App.Reader.Flashcard.Report.ReasonOffensive') },
    { value: 'LowQuality', label: t('App.Reader.Flashcard.Report.ReasonLowQuality') },
    { value: 'Other',      label: t('App.Reader.Flashcard.Report.ReasonOther') },
  ];

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(reason, notes.trim() || undefined);
      onClose();
      setNotes('');
      setReason('Incorrect');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1100] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="report-title"
           className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl p-4 sm:p-6 shadow-xl">
        <h3 id="report-title" className="text-lg font-semibold mb-3">{t('App.Reader.Flashcard.Report.Title')}</h3>
        <fieldset className="space-y-2 mb-4">
          <legend className="text-sm font-medium mb-2">{t('App.Reader.Flashcard.Report.ReasonLabel')}</legend>
          {REASONS.map((r) => (
            <label key={r.value} className="flex items-center gap-2 py-2 min-h-[44px] cursor-pointer">
              <input
                type="radio" name="reason" value={r.value}
                checked={reason === r.value}
                onChange={() => setReason(r.value)}
                className="w-5 h-5"
              />
              <span>{r.label}</span>
            </label>
          ))}
        </fieldset>
        <label className="block mb-4">
          <span className="text-sm font-medium">{t('App.Reader.Flashcard.Report.NotesLabel')}</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
            rows={3}
            className="mt-1 w-full border rounded p-2 text-sm"
          />
        </label>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} disabled={submitting}
                  className="min-h-[44px] px-4 py-2 rounded border">{t('App.Reader.Flashcard.Report.CancelButton')}</button>
          <button onClick={handleSubmit} disabled={submitting}
                  className="min-h-[44px] px-4 py-2 rounded bg-red-600 text-white disabled:opacity-50">
            {submitting ? t('App.Reader.Flashcard.Report.Submitting') : t('App.Reader.Flashcard.Report.SubmitButton')}
          </button>
        </div>
      </div>
    </div>
  );
}
